import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  PROBLEM,
  INTUITION,
  APPROACH,
  TOOL,
  HONESTY,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK: the structural question vector search cannot answer */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const glow = interpolate(frame, [40, 75], [0.3, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="code intelligence · graph_query" color={COLORS.cyan} />
          <Big>
            My RAG can answer
            <br />
            <span style={{ color: COLORS.cyan, opacity: glow }}>
              “who calls this function?”
            </span>
          </Big>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 26,
              color: COLORS.dim,
              marginTop: 34,
            }}
          >
            vector search can’t · cosine ranks meaning, not structure
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM: edges, not embeddings */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="structural questions" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="cyan" title="vector top-k · by meaning" width={500}>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 22,
                lineHeight: 1.9,
                color: COLORS.text,
              }}
            >
              <div>“who calls handleAuth?”</div>
              <div style={{ color: COLORS.dim }}>↳ authNotes.md 0.71</div>
              <div style={{ color: COLORS.dim }}>↳ loginGuide.md 0.68</div>
              <div style={{ color: COLORS.red, marginTop: 6 }}>
                ✗ no actual callers
              </div>
            </div>
          </Panel>
          <Panel accent="green" title="graph traversal · by edges" width={500}>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 22,
                lineHeight: 1.9,
                color: COLORS.text,
              }}
            >
              <div>reverse-BFS over CALLS</div>
              <div style={{ color: COLORS.green }}>↳ sessionMiddleware</div>
              <div style={{ color: COLORS.green }}>↳ refreshToken</div>
              <div style={{ color: COLORS.green, marginTop: 6 }}>
                ✓ the real callers
              </div>
            </div>
          </Panel>
        </div>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 28,
            color: COLORS.dim,
            marginTop: 30,
          }}
        >
          who-calls / path-between / blast-radius are <b style={{ color: COLORS.text }}>edges</b>, not embeddings
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — INTUITION: the graph already lives in the chunk metadata */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 36, alignItems: "center" }}>
          <Big size={62}>entities + relationships</Big>
          <div style={{ fontFamily: FONTS.display, fontSize: 54, color: COLORS.dim }}>→</div>
          <Big size={62}>
            <span style={{ color: COLORS.violet }}>a call graph</span>
          </Big>
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 26,
            color: COLORS.dim,
            marginTop: 30,
          }}
        >
          already extracted at ingest into document_chunks.metadata_graph
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — APPROACH: the four-stage pipeline */
const PIPE: PipeNode[] = [
  { label: "1 · classify", value: "regex-first", sub: "LLM fallback", accent: "cyan" },
  { label: "2 · translate", value: "NL → DSL", sub: "deterministic", accent: "green" },
  { label: "3 · traverse", value: "BFS", sub: "metadata_graph", accent: "violet" },
  { label: "4 · render", value: "[Source N]", sub: "no LLM", accent: "amber" },
];
export const Scene04Approach: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.approach.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="the bypass pipeline · classify → cite" color={COLORS.green} />
      <div style={{ width: 1680 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={22} nodeWidth={220} />
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 22,
          color: COLORS.dim,
          marginTop: 40,
        }}
      >
        keyword-classified, not LLM-classified — the LLM only breaks ties
      </div>
    </AbsoluteFill>
    <Caption lines={APPROACH} accent="green" />
  </AbsoluteFill>
);

/* 05 — TOOL: graph_query, the 5 ops, tenant isolation, cited output */
const TOOL_CODE = `// src/lib/mcp/tools.ts — graph_query (MCP tool)
{
  name: 'graph_query',
  // Graphify traversal over the tenant's metadata_graph.
  // Ops: whoCalls · pathBetween · dependents
  //      impactRadius · mostConnected
  inputSchema: {
    query: z.string().trim().min(3).max(500),
  },
}

// tenant isolation threaded all the way down:
await summariseGraphOnly({ query, tenantId, ... })
// → getOrBuild(tenantId, { sourceIds }) — scoped store

// returns markdown + inline [Source N] citations,
// plus a JSON block of { nodes, edges, citations }`;
export const Scene05Tool: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.tool.duration} />
    <AbsoluteFill
      style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}
    >
      <CodePanel
        code={TOOL_CODE}
        language="tsx"
        filename="src/lib/mcp/tools.ts"
        highlightLines={[5, 6, 9, 14]}
        revealFrames={300}
        fontSize={23}
        width={1300}
      />
    </AbsoluteFill>
    <Caption lines={TOOL} accent="cyan" />
  </AbsoluteFill>
);

/* 06 — HONESTY: jsonb not a graph DB; AGE rejected; RAG fallback */
export const Scene06Honesty: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [40, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const c2 = interpolate(frame, [110, 140], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.honesty.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="honest scope · what this is NOT" color={COLORS.amber} />
          <Big size={64}>
            no separate graph database
          </Big>
          <div style={{ display: "flex", gap: 30, marginTop: 44 }}>
            <div style={{ opacity: c1 }}>
              <Callout label="how it’s stored" accent="amber" width={420}>
                in-memory adjacency list, rebuilt on demand from the{" "}
                <b style={{ color: COLORS.text }}>metadata_graph jsonb</b> column · Apache AGE was rejected
              </Callout>
            </div>
            <div style={{ opacity: c2 }}>
              <Callout label="when it’s not graph-shaped" accent="cyan" width={420}>
                returns <b style={{ color: COLORS.text }}>isError: false</b> · “no graph answer available” → falls back to normal RAG
              </Callout>
            </div>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HONESTY} accent="amber" />
    </AbsoluteFill>
  );
};

/* 07 — PAYOFF: deterministic, no LLM, p95 <4s */
export const Scene07Payoff: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
    <AbsoluteFill style={center}>
      <Kicker text="why it stays fast" color={COLORS.lime} />
      <div style={{ display: "flex", gap: 28, marginTop: 6 }}>
        <Panel accent="green" title="traversal" width={360}>
          <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.5, color: COLORS.text }}>
            one BFS walk over the in-memory store
          </div>
        </Panel>
        <Panel accent="lime" title="answer" width={360}>
          <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.5, color: COLORS.text }}>
            pure markdown assembly · <b style={{ color: COLORS.lime }}>no LLM</b>
          </div>
        </Panel>
        <Panel accent="cyan" title="target" width={360}>
          <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.5, color: COLORS.text }}>
            p95 <b style={{ color: COLORS.cyan }}>&lt; 4s</b> on graph queries (in-code goal)
          </div>
        </Panel>
      </div>
    </AbsoluteFill>
    <Caption lines={PAYOFF} accent="lime" />
  </AbsoluteFill>
);

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          Vector finds what’s <span style={{ color: COLORS.cyan }}>similar</span>.
          <br />
          A graph answers what’s <span style={{ color: COLORS.violet }}>connected</span>.
          <br />
          A real RAG needs both.
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · build in public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
